var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { handleErrorDialog } from '@juvare/common-web-ui/DialogHelper';
import { BaseWidgetEditorComponent } from '@juvare/widget-ui/BaseWidgetEditorComponent';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import MapOutlinedIcon from '@mui/icons-material/MapOutlined';
import { Box, Button, FormControl, Typography } from '@mui/material';
import React from 'react';
import { buildEventTab, buildGeneralTab, buildHiddenTab, buildStyleTab, buildValidateTab, showWidgetEditDialog } from '../WidgetEditDialog';
import { FormHelperErrorDescription } from '../WidgetForm/Components';
import { defaultWidgetLabelProvider, getVisibleError, handleWidgetUpdate, isSkipValidate, makeNewConfigUnique, renderForm, validateCustom, } from '../WidgetForm/Utils';
import { ArcgisAutocompleteSearch } from './components/ArcgisAutocompleteSearch';
import { showArcgisAddressDialog } from './components/showArcgisAddressDialog';
import { type } from './types';
import { defaultCoordinates, getAddressFieldsInternal, getAddressString, isCoordinates, parseCoordinates } from './utils';
const WidgetFormArcgisAddress = (props) => {
    var _a, _b, _c, _d, _e, _f;
    const geocoderService = (_a = props.rootParams.globalWidgetConfig) === null || _a === void 0 ? void 0 : _a.geocoderService;
    const error = getVisibleError(props);
    const [autocompleteKey, setAutocompleteKey] = React.useState(0);
    const readOnly = props.config.readOnly || ((_b = props.rootParams.formParams) === null || _b === void 0 ? void 0 : _b.readOnly);
    /* eslint-disable @typescript-eslint/no-explicit-any */
    const handleSubmit = (data) => {
        var _a, _b;
        const address = data
            ? getAddressFieldsInternal(((_a = props.internalData.value) === null || _a === void 0 ? void 0 : _a.useLocation) ? data.address : data.attributes, data.location)
            : undefined;
        if ((_b = props.internalData.value) === null || _b === void 0 ? void 0 : _b.useLocation) {
            props.internalData.value.useLocation = false;
        }
        props.setData(address);
        setAutocompleteKey((prev) => ++prev);
        renderForm(props, { changed: true, autoSaveDebounce: props.config.autoSave });
    };
    const handleGetLocation = (value) => __awaiter(void 0, void 0, void 0, function* () {
        if (!value) {
            return;
        }
        try {
            let data;
            if (isCoordinates(value)) {
                const { latitude, longitude } = parseCoordinates(value);
                // coordinates value in the request to service is the inverse of form input:
                // we enter "lat,long" but the request expects "long,lat"
                data = yield geocoderService.reverseGeocode({ location: `${longitude},${latitude}` });
                props.internalData.value = {
                    useLocation: true,
                };
            }
            else {
                // if value is not coordinates, it is going to be one of the suggestions
                data = yield geocoderService.findAddressCandidates({ SingleLine: encodeURIComponent(value) });
            }
            props.update();
            return data;
        }
        catch (err) {
            handleErrorDialog(err);
        }
    });
    const handleSubmitOnValueChange = (value) => __awaiter(void 0, void 0, void 0, function* () {
        const data = yield handleGetLocation(value);
        handleSubmit(data);
    });
    const ariaDescribedBy = React.useId();
    if (!geocoderService) {
        return React.createElement(Typography, null, "Cannot find geocoder service in globalWidgetConfig.");
    }
    const value = props.getData();
    const initialAddress = (_c = getAddressString(value)) !== null && _c !== void 0 ? _c : undefined;
    const initialCoordinates = (value === null || value === void 0 ? void 0 : value.location) ? `${value.location.longitude},${value.location.latitude}` : defaultCoordinates;
    return (React.createElement(FormControl, { fullWidth: true, required: (_d = props.config.validate) === null || _d === void 0 ? void 0 : _d.required, disabled: props.config.disabled, error: !!error },
        React.createElement(Box, { sx: { display: 'flex', width: '100%', gap: 1 } },
            React.createElement(Box, { sx: { flex: 1 } },
                React.createElement(ArcgisAutocompleteSearch, { key: `arcgis-root-autocomplete-key-${autocompleteKey}`, required: (_e = props.config.validate) === null || _e === void 0 ? void 0 : _e.required, autoFocus: (_f = (!props.editMode && props.config.autoFocus)) !== null && _f !== void 0 ? _f : false, readOnly: readOnly, disabled: props.config.disabled, editMode: props.editMode, label: props.config.label, error: !!error, onChange: handleSubmitOnValueChange, gisClient: geocoderService, initialInputValue: initialAddress, ariaDescribedBy: ariaDescribedBy })),
            React.createElement(Button, { disabled: props.config.disabled, variant: 'outlined', onClick: () => {
                    var _a;
                    return showArcgisAddressDialog({
                        onChange: handleGetLocation,
                        onSubmit: handleSubmit,
                        gisClient: geocoderService,
                        error: error,
                        label: props.config.dialogLabel,
                        disabled: props.config.disabled,
                        description: props.config.description,
                        required: (_a = props.config.validate) === null || _a === void 0 ? void 0 : _a.required,
                        readOnly: readOnly,
                        initialInputValue: initialAddress,
                        initialCoordinates,
                    });
                }, endIcon: React.createElement(MapOutlinedIcon, { fontSize: 'medium' }) }, "MAP")),
        React.createElement(FormHelperErrorDescription, { descriptionId: ariaDescribedBy, error: error, description: props.config.description })));
};
const WidgetFormArcgisAddressEditor = (props) => {
    return (React.createElement(BaseWidgetEditorComponent, Object.assign({}, props, { widgetLabelProvider: defaultWidgetLabelProvider, onEditWidget: (endEditing) => showWidgetEditDialog(props, endEditing, { title: props.config.label }, buildGeneralTab(props, {
            key: true,
            label: true,
            appendRowIndexToLabel: true,
            description: true,
            disabled: true,
            readOnly: true,
            autoFocus: true,
            children: [
                {
                    type: 'form.text',
                    key: 'dialogLabel',
                    label: "Label Used in the Dialog's Search Input ",
                },
            ],
        }), buildValidateTab(props, { required: true }), buildHiddenTab(props), buildStyleTab(props), buildEventTab(props, { eventOnChange: true, autoSave: true })) }),
        React.createElement(WidgetFormArcgisAddress, Object.assign({}, props))));
};
export const widgetFormArcgisAddress = {
    type,
    update: handleWidgetUpdate,
    validate: (props) => {
        var _a, _b;
        if (isSkipValidate(props)) {
            return;
        }
        const value = props.getData();
        if ((_a = props.config.validate) === null || _a === void 0 ? void 0 : _a.required) {
            if (!value) {
                return (_b = props.config.validate.requiredMessage) !== null && _b !== void 0 ? _b : (props.config.label ? props.config.label + ' is required' : 'Required');
            }
        }
        return validateCustom(props, value);
    },
    component: WidgetFormArcgisAddress,
    icon: React.createElement(FormatListBulletedIcon, { fontSize: 'small' }),
    editorComponent: WidgetFormArcgisAddressEditor,
    createWidget: (rootConfig, editorPath, widgets) => makeNewConfigUnique(rootConfig, editorPath, widgets, {
        type,
        key: 'arcgisAddress',
        label: 'Address',
        dialogLabel: 'Search Address or Coordinates',
    }),
};
